/**
 * Which event-detection sensor types a mote can actually use.
 *
 * Hidden, not disabled: a condition on a channel the mote does not sample can
 * never fire, so offering it would only produce a dead rule.
 */

export const MAGNETOMETER_METRIC = 'Magnetometer';
export const TEMPERATURE_METRIC = 'Temperature';
export const SPEED_METRIC_TYPE = 'Speed';
export const OUTSIDE_RANGE_OPERATOR = 'OUTSIDE';

/**
 * @param {string} metricValue      sensor type name, e.g. 'Magnetometer'
 * @param {Object} options
 * @param {boolean} options.temperatureOnly        mote is in temperature-only mode
 * @param {boolean} options.magnetometerAvailable  magnetometer channel is enabled
 * @param {string}  options.currentMetric          sensor type this condition already uses
 */
export const isEventMetricAvailable = (metricValue, {
  temperatureOnly = false,
  magnetometerAvailable = true,
  currentMetric = null
} = {}) => {
  if (temperatureOnly) {
    return metricValue === TEMPERATURE_METRIC;
  }

  if (metricValue === MAGNETOMETER_METRIC) {
    // A rule saved before the channel was turned off keeps its option, or the
    // dropdown renders blank with no way to see what the condition is.
    return magnetometerAvailable || currentMetric === MAGNETOMETER_METRIC;
  }

  return true;
};

/**
 * Outside Range is hidden on Speed, not rejected: the backend still accepts it
 * and the device still evaluates it, so anything already saved keeps working.
 *
 * @param {string} operatorValue   operator enum value, e.g. 'OUTSIDE'
 * @param {Object} options
 * @param {string} options.metricType       parameter this condition uses
 * @param {string} options.currentOperator  operator this condition already uses
 */
export const isEventOperatorAvailable = (operatorValue, {
  metricType = null,
  currentOperator = null
} = {}) => {
  if (operatorValue !== OUTSIDE_RANGE_OPERATOR) {
    return true;
  }

  if (metricType !== SPEED_METRIC_TYPE) {
    return true;
  }

  // A rule saved with it keeps the option, or the dropdown renders blank.
  return currentOperator === OUTSIDE_RANGE_OPERATOR;
};

export const DEFAULT_EVENT_OPERATOR = '>';

/**
 * What a metric type switch does to an operator the new type does not offer.
 * It resets to the default operator and a zero value, so the user sees the
 * change. Turning Outside Range into Inside Range kept both numbers and
 * silently inverted the rule.
 *
 * @returns {Object|null} condition patch, or null when the operator stays
 */
export const operatorResetForMetricType = (operator, metricType) => (
  isEventOperatorAvailable(operator, { metricType, currentOperator: null })
    ? null
    : { operator: DEFAULT_EVENT_OPERATOR, thresholds: { value: 0 } }
);

export default isEventMetricAvailable;

/**
 * The editor owns event_configs and enabled. Everything else on event_detection
 * comes from the server and describes the mote, not the rules — speed_limits
 * and magnetometer_available among them.
 *
 * Rebuilding the object from scratch on every keystroke dropped those: the band
 * check silently became a no-op and the magnetometer option reappeared on a mote
 * whose channel is off. Nothing restores them, because the parent only re-syncs
 * from props when the sensor itself changes.
 *
 * Safe to carry the extra keys: the save path reads only event_configs and
 * enabled, and the dirty check normalises through an explicit whitelist.
 */
export const mergeEventDetectionChange = (
  eventDetection, newConfigs, enabled, measurementLocationId
) => ({
  ...(eventDetection || {}),
  event_configs: newConfigs,
  enabled,
  measurement_location_id: measurementLocationId
});
